import { useState, useEffect, useCallback } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Download, AlertTriangle, Mail, Send, BellRing } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../components/ui/tabs-variants";
import { EmployeeList, EmployeeFormDialog, ExportDialog, PushTab, ResultDialog } from "../components/employees";
import { splitFullName, isoToMmddyyyy, mmddyyyyToIso } from "../lib/formatters";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
// ===== Email Tab Component =====
const EmailTab = ({ getAuthHeaders }) => {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get(`/communications/employees-contacts`);
        setContacts(res.data);
      } catch { toast.error("Failed to load contacts"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [getAuthHeaders]);

  const emailContacts = contacts.filter(c => c.has_email);
  const allSelected = emailContacts.length > 0 && selected.length === emailContacts.length;

  const toggleAll = () => {
    setSelected(allSelected ? [] : emailContacts.map(c => c.id));
  };

  const toggleOne = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || selected.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await apiClient.post(`/communications/send-email`, {
        employee_ids: selected,
        subject: subject.trim(),
        body: body.trim(),
      });
      setResult(res.data);
      if (res.data.sent_count > 0) toast.success(`Email sent to ${res.data.sent_count} employee(s)`);
      if (res.data.failed_count > 0) toast.error(`${res.data.failed_count} failed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  if (loading) return <div className="animate-pulse text-muted-foreground p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" /> Send Email
          </CardTitle>
          <p className="text-sm text-muted-foreground">Send an email to one or more employees</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Email subject..."
              data-testid="email-subject-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Message *</Label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Type your message..."
              rows={5}
              data-testid="email-body-input"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Recipients ({selected.length} selected)</Label>
              <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="email-select-all">
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            </div>
            {emailContacts.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No employees have email addresses on file</p>
            ) : (
              <div className="border rounded-lg max-h-[300px] overflow-y-auto divide-y">
                {emailContacts.map(c => (
                  <label key={c.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer" data-testid={`email-contact-${c.id}`}>
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleOne(c.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{c.role}</Badge>
                  </label>
                ))}
              </div>
            )}
            {contacts.filter(c => !c.has_email).length > 0 && (
              <p className="text-xs text-muted-foreground">
                {contacts.filter(c => !c.has_email).length} employee(s) have no email address and are not shown
              </p>
            )}
          </div>

          <Button onClick={handleSend} disabled={sending || !subject.trim() || !body.trim() || selected.length === 0} className="w-full" data-testid="send-email-btn">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : `Send Email to ${selected.length} Recipient(s)`}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <ResultDialog result={result} type="Email" onClose={() => setResult(null)} />
      )}
    </div>
  );
};

// ===== Main Employees Page =====
const EmployeesPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [employeeRoles, setEmployeeRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({ start: "", end: "" });
  const [exportFilter, setExportFilter] = useState("active");
  const [exportFormat, setExportFormat] = useState("excel");
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  const canCommunicate = ["admin", "project_manager", "office_manager"].includes(user?.role);

  const [form, setForm] = useState({
    name: "", first_name: "", last_name: "", date_of_birth: "", role: "", hourly_rate: "", phone: "", email: "",
    street: "", apt: "", city: "", state: "", zip_code: "",
    hire_date: "", separation_date: "", is_active: true, rehireable: null,
    is_field: false, is_office: false, base_access: null,
    emergency_contact: { name: "", relationship: "", phone: "" },
    ssn_last4: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [employeesRes, optionsRes] = await Promise.all([
        apiClient.get(`/employees`),
        apiClient.get(`/settings/options`),
      ]);
      setEmployees(employeesRes.data);
      setEmployeeRoles(optionsRes.data.employee_roles || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({
      name: "", first_name: "", last_name: "", date_of_birth: "", role: "", hourly_rate: "", phone: "", email: "",
      street: "", apt: "", city: "", state: "", zip_code: "",
      hire_date: "", separation_date: "", is_active: true, rehireable: null,
      is_field: false, is_office: false, base_access: null,
      emergency_contact: { name: "", relationship: "", phone: "" },
      ssn_last4: "",
    });
    setEditingEmployee(null);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    const names = employee.first_name || employee.last_name
      ? { first_name: employee.first_name || "", last_name: employee.last_name || "" }
      : splitFullName(employee.name);
    setForm({
      name: employee.name, ...names, date_of_birth: isoToMmddyyyy(employee.date_of_birth), role: employee.role,
      hourly_rate: employee.hourly_rate != null ? employee.hourly_rate.toString() : "",
      phone: employee.phone || "", email: employee.email || "",
      street: employee.street || "", apt: employee.apt || "",
      city: employee.city || "", state: employee.state || "",
      zip_code: employee.zip_code || "", hire_date: employee.hire_date || "",
      separation_date: employee.separation_date || "",
      is_active: employee.is_active, rehireable: employee.rehireable,
      is_field: employee.is_field || false, is_office: employee.is_office || false,
      base_access: employee.base_access ?? null,
      emergency_contact: {
        name: employee.emergency_contact?.name || "",
        relationship: employee.emergency_contact?.relationship || "",
        phone: employee.emergency_contact?.phone || "",
      },
      ssn_last4: employee.ssn_last4 || "",
    });
    setDialogOpen(true);
  };

  const fullName = () => `${(form.first_name || "").trim()} ${(form.last_name || "").trim()}`.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.date_of_birth && !mmddyyyyToIso(form.date_of_birth)) {
      toast.error("Date of birth must be a valid date in MM/DD/YYYY format");
      return;
    }
    if (!editingEmployee && fullName()) {
      try {
        const { data } = await apiClient.get(`/employees/check-duplicate`, {
          params: { name: fullName() }
        });
        if (data.has_duplicates) {
          setDuplicateWarning(data.duplicates);
          setPendingSubmit(e);
          return;
        }
      } catch (err) {
        // Duplicate check is best-effort — log and proceed with submit
        console.warn("EmployeesPage: duplicate-check failed, proceeding:", err);
      }
    }
    await submitEmployee();
  };

  const submitEmployee = async () => {
    try {
      const payload = {
        ...form,
        name: fullName(),
        first_name: (form.first_name || "").trim(),
        last_name: (form.last_name || "").trim(),
        date_of_birth: mmddyyyyToIso(form.date_of_birth) || null,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : (form.is_office ? 0 : null),
        rehireable: form.is_active ? null : form.rehireable,
      };
      if (editingEmployee) {
        await apiClient.put(`/employees/${editingEmployee.id}`, payload);
        toast.success("Employee updated");
      } else {
        await apiClient.post(`/employees`, payload);
        toast.success("Employee added");
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
      setDuplicateWarning(null);
      setPendingSubmit(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDuplicateConfirm = async () => { setDuplicateWarning(null); setPendingSubmit(null); await submitEmployee(); };
  const handleDuplicateCancel = () => { setDuplicateWarning(null); setPendingSubmit(null); };

  const handleExport = async () => {
    const employeesToExport = exportFilter === "active" ? activeEmployees : sortedEmployees;
    let filteredEmployees = employeesToExport;
    if (exportDateRange.start || exportDateRange.end) {
      filteredEmployees = employeesToExport.filter(emp => {
        if (!emp.hire_date) return false;
        const hireDate = new Date(emp.hire_date);
        return (!exportDateRange.start || hireDate >= new Date(exportDateRange.start)) &&
               (!exportDateRange.end || hireDate <= new Date(exportDateRange.end));
      });
    }
    if (exportFormat === "excel") {
      try {
        let url = `/employees/export`;
        const params = new URLSearchParams();
        if (exportDateRange.start) params.append("start_date", exportDateRange.start);
        if (exportDateRange.end) params.append("end_date", exportDateRange.end);
        params.append("filter", exportFilter);
        params.append("format", "excel");
        url += `?${params.toString()}`;
        const response = await apiClient.get(url, { responseType: "blob" });
        const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a"); link.href = downloadUrl;
        link.download = `employees_export_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success("Excel export downloaded");
        closeExportDialog();
      } catch { toast.error("Failed to export employees"); }
    } else if (exportFormat === "pdf") {
      const getEmployeeType = (emp) => {
        const types = [];
        if (emp.is_office) types.push("Office");
        if (emp.is_field) types.push(emp.base_access ? "Field (Base Access)" : "Field (No Base Access)");
        return types.length > 0 ? types.join(" / ") : "Not specified";
      };
      const printContent = `<!DOCTYPE html><html><head><title>Employee Report - V2R Masonry</title><style>
        @media print { .page-break { page-break-after: always; } }
        body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #333; }
        .header h1 { font-size: 24px; margin: 0; } .header .subtitle { color: #666; font-size: 12px; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
        .card { border: 1px solid #ddd; border-radius: 8px; padding: 15px; page-break-inside: avoid; break-inside: avoid; }
        .card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #eee; }
        .card-name { font-size: 14px; font-weight: bold; }
        .card-role { font-size: 11px; background: #e2e8f0; padding: 2px 6px; border-radius: 4px; }
        .detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px; }
        .detail-item { font-size: 10px; } .detail-label { color: #666; } .detail-value { font-weight: 500; }
        .footer { text-align: center; margin-top: 30px; color: #666; font-size: 11px; }
      </style></head><body>
        <div class="header"><div><h1>V2R Masonry - Employee Report</h1><div class="subtitle">Generated: ${new Date().toLocaleDateString()}</div></div></div>
        ${(() => {
          const pages = []; const perPage = 6;
          for (let i = 0; i < filteredEmployees.length; i += perPage) {
            const pageEmps = filteredEmployees.slice(i, i + perPage);
            pages.push(`<div class="${i + perPage < filteredEmployees.length ? 'page-break' : ''}"><div class="grid">
              ${pageEmps.map(emp => `<div class="card"><div class="card-header"><span class="card-name">${emp.name}</span><span class="card-role">${emp.role || 'N/A'}</span></div>
                <div class="detail-grid">
                  <div class="detail-item"><div class="detail-label">Type</div><div class="detail-value">${getEmployeeType(emp)}</div></div>
                  <div class="detail-item"><div class="detail-label">Rate</div><div class="detail-value">${emp.hourly_rate != null ? '$'+emp.hourly_rate.toFixed(2)+'/hr' : 'N/A'}</div></div>
                  <div class="detail-item"><div class="detail-label">Phone</div><div class="detail-value">${emp.phone || '-'}</div></div>
                  <div class="detail-item"><div class="detail-label">Email</div><div class="detail-value" style="font-size:9px;word-break:break-all">${emp.email || '-'}</div></div>
                  <div class="detail-item"><div class="detail-label">Hire Date</div><div class="detail-value">${emp.hire_date || '-'}</div></div>
                  <div class="detail-item"><div class="detail-label">Status</div><div class="detail-value">${emp.is_active ? 'Active' : 'Inactive'}</div></div>
                  ${!emp.is_active ? `<div class="detail-item"><div class="detail-label">Separation Date</div><div class="detail-value">${emp.separation_date || '-'}</div></div>
                  <div class="detail-item"><div class="detail-label">Re-Hireable</div><div class="detail-value">${emp.rehireable === true ? 'Yes' : emp.rehireable === false ? 'No' : '-'}</div></div>` : ''}
                </div></div>`).join('')}
            </div></div>`);
          }
          return pages.join('');
        })()}
        <p class="footer">Total: ${filteredEmployees.length} employee${filteredEmployees.length !== 1 ? 's' : ''} | V2R Masonry</p>
      </body></html>`;
      const printWindow = window.open('', '_blank');
      if (!printWindow) { toast.error("Popup blocked. Please allow popups."); return; }
      printWindow.document.write(printContent); printWindow.document.close(); printWindow.focus();
      setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
      closeExportDialog();
      toast.success("PDF export opened for printing");
    }
  };

  const closeExportDialog = () => {
    setExportDialogOpen(false);
    setExportDateRange({ start: "", end: "" });
    setExportFilter("active");
    setExportFormat("excel");
  };

  const sortedEmployees = [...employees].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
  const activeEmployees = sortedEmployees.filter(e => e.is_active);
  const inactiveEmployees = sortedEmployees.filter(e => !e.is_active);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-pulse text-muted-foreground">Loading employees...</div></div>;
  }

  return (
    <div className="page-enter space-y-8" data-testid="employees-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">Employees</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setExportDialogOpen(true)} data-testid="export-employees-btn">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />Add Employee
          </Button>
        </div>
      </div>

      <Tabs defaultValue="employees" className="space-y-6">
        <TabsList data-testid="employees-tabs">
          <TabsTrigger value="employees" data-testid="employees-tab">
            <Users className="w-4 h-4 mr-2" />Employees
          </TabsTrigger>
          {canCommunicate && (
            <TabsTrigger value="email" data-testid="email-tab">
              <Mail className="w-4 h-4 mr-2" />Email
            </TabsTrigger>
          )}
          {canCommunicate && (
            <TabsTrigger value="push" data-testid="push-tab">
              <BellRing className="w-4 h-4 mr-2" />Push
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="employees">
          {employees.length === 0 ? (
            <Card className="p-12">
              <div className="text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No employees yet</h3>
                <p className="text-muted-foreground mb-6">Add your team members to start tracking labor costs</p>
                <Button onClick={() => setDialogOpen(true)}><Plus className="w-4 h-4 mr-2" />Add First Employee</Button>
              </div>
            </Card>
          ) : (
            <EmployeeList
              activeEmployees={activeEmployees}
              inactiveEmployees={inactiveEmployees}
              inactiveOpen={inactiveOpen}
              setInactiveOpen={setInactiveOpen}
              onEdit={openEditDialog}
            />
          )}
        </TabsContent>

        {canCommunicate && (
          <TabsContent value="email">
            <EmailTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        )}
        {canCommunicate && (
          <TabsContent value="push">
            <PushTab />
          </TabsContent>
        )}
      </Tabs>

      <EmployeeFormDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}
        editingEmployee={editingEmployee}
        form={form} setForm={setForm}
        employeeRoles={employeeRoles}
        onSubmit={handleSubmit}
      />

      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={(open) => { if (!open) closeExportDialog(); else setExportDialogOpen(true); }}
        exportDateRange={exportDateRange} setExportDateRange={setExportDateRange}
        exportFilter={exportFilter} setExportFilter={setExportFilter}
        exportFormat={exportFormat} setExportFormat={setExportFormat}
        activeCount={activeEmployees.length} totalCount={sortedEmployees.length}
        onExport={handleExport}
      />

      <AlertDialog open={!!duplicateWarning} onOpenChange={(open) => !open && handleDuplicateCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />Possible Duplicate Employee
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>An employee with a similar name already exists:</p>
                <div className="bg-muted rounded-lg p-3 space-y-2">
                  {duplicateWarning?.map((dup, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="font-medium text-foreground">{dup.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${dup.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {dup.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-sm">Do you still want to add <strong>&quot;{form.name}&quot;</strong> as a new employee?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDuplicateCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicateConfirm} className="bg-amber-500 hover:bg-amber-600">Add Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployeesPage;
